// EN-10064 — Colluder-block client contract types.
//
// The server (player-api `GET /player/colluder-restrictions` + the
// `gotColluderRestrictions` WebSocket push) reports the viewer's restricted
// lobby entries as three ID lists. The lists are ID-only by design: they never
// carry the opponent's identity, the reason, or who flagged them
// (no-disclosure / BR-3). The client treats each payload as the full current
// set (NOT a diff) and replaces local marker state wholesale.

/** The viewer's restricted lobby entries, by scope. IDs are normalized to strings. */
export interface ColluderRestrictions {
  /** Cash game IDs the viewer can't seat at — union of texas + omaha (backward compat). */
  restrictedCash: string[];
  /** Texas Hold'em cash game IDs the viewer can't seat at. */
  restrictedTexasCG: string[];
  /** Omaha cash game IDs the viewer can't seat at. */
  restrictedOmahaCG: string[];
  /** Sit & Go tournament IDs the viewer can't register for. */
  restrictedSng: string[];
  /** MTT tournament IDs the viewer can't register for. */
  restrictedMtt: string[];
}

export const EMPTY_COLLUDER_RESTRICTIONS: ColluderRestrictions = {
  restrictedCash: [],
  restrictedTexasCG: [],
  restrictedOmahaCG: [],
  restrictedSng: [],
  restrictedMtt: []
};

const toIdList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  // IDs arrive as numbers (gameID / tournamentId) or strings depending on the
  // source. Normalize to strings so they compare cleanly against the lobby
  // tile ids (CashGame.id / Tournament.id are strings). Drop empty / nullish.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value) {
    if (raw === null || raw === undefined) continue;
    const id = String(raw).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

/**
 * Normalize an arbitrary wire payload into a `ColluderRestrictions`. Tolerates
 * both the bare shape `{ restrictedCash, restrictedSng, restrictedMtt }` and an
 * enveloped `{ response: { ... } }` form, plus missing scopes (treated as
 * empty — clears that scope's markers).
 */
export const normalizeColluderRestrictions = (decoded: unknown): ColluderRestrictions => {
  if (!decoded || typeof decoded !== 'object') return { ...EMPTY_COLLUDER_RESTRICTIONS };
  const obj = decoded as Record<string, unknown>;
  const source =
    obj.response && typeof obj.response === 'object'
      ? (obj.response as Record<string, unknown>)
      : obj;
  return {
    restrictedCash: toIdList(source.restrictedCash),
    restrictedTexasCG: toIdList(source.restrictedTexasCG),
    restrictedOmahaCG: toIdList(source.restrictedOmahaCG),
    restrictedSng: toIdList(source.restrictedSng),
    restrictedMtt: toIdList(source.restrictedMtt)
  };
};

/**
 * The `gotJoinGame` hard-block payload (`response`) the engine sends when a
 * seat is rejected because a flagged opponent is at the table. Only the
 * `code === 'INTEGRITY_RESTRICTION'` case routes to the integrity modal; all
 * other `gotJoinGame` errors keep the generic error path.
 */
export interface IntegrityBlockResponse {
  error?: string;
  restricted?: boolean;
  code?: string;
  scope?: string;
  gameId?: number | string;
  // The engine sends the table's game type so the client can route a waitlist
  // join without reverse-engineering it from the lobby list (texas/omaha
  // socket handlers know it authoritatively at block time).
  gameType?: string;
  waitlistAvailable?: boolean;
}

export const INTEGRITY_RESTRICTION_CODE = 'INTEGRITY_RESTRICTION';

// EN-10064 — canonical player-facing copy. ONE consistent restriction treatment
// and message across every surface (cash, SnG, MTT) so the player sees the same
// marker + modal regardless of what they tap. Surface-neutral wording (no
// "table"/"tournament") keeps it accurate everywhere. No-disclosure (BR-3):
// none names the opponent, the reason, or who flagged them.
export const LBL_RESTRICTED_TITLE_TABLE = 'TABLE RESTRICTED';
export const LBL_RESTRICTED_TITLE_TOURNAMENT = 'TOURNAMENT RESTRICTED';
export const LBL_RESTRICTED_SUB = 'Platform integrity measure';
// Hard-block modal copy. Title + body are surface-aware (table vs tournament);
// keep the titles one-liners.
export const MODAL_HEADER_LABEL = 'Platform integrity';
export const MODAL_TITLE_TABLE = "You can't be seated at this table.";
export const MODAL_TITLE_TOURNAMENT = "You can't be seated at this tournament.";
export const MODAL_BODY_TABLE =
  "A player you're restricted from sharing tables with is seated here.";
export const MODAL_BODY_TOURNAMENT =
  "A player you're restricted from sharing tables with is registered here.";

/** True when a `gotJoinGame` response is the colluder integrity hard-block. */
export const isIntegrityBlock = (response: unknown): response is IntegrityBlockResponse =>
  !!response &&
  typeof response === 'object' &&
  (response as { code?: unknown }).code === INTEGRITY_RESTRICTION_CODE;
